import type { AdminActivity, FeatureDraft, FeaturePortal, MediaSelectCheckoutItem, MediaUploadOptions, ReleasePortal } from '../types';
import { demoPortal } from './demo';
import { demoMode, supabase } from './supabase';

const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

async function token() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const accessToken = await token();
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    const raw = await response.text();
    try {
      const body = JSON.parse(raw) as { error?: string };
      throw new Error(body.error || raw || `Request failed (${response.status})`);
    } catch (error) {
      if (error instanceof Error && !error.message.startsWith('Unexpected token')) throw error;
      throw new Error(raw || `Request failed (${response.status})`);
    }
  }
  return response.json() as Promise<T>;
}

export async function getPortal(slug: string): Promise<FeaturePortal> {
  if (demoMode) return demoPortal;
  return apiFetch<FeaturePortal>(`/api/portal/${encodeURIComponent(slug)}`);
}

export async function getRelease(slug: string): Promise<ReleasePortal> {
  if (demoMode) {
    return {
      id: demoPortal.id,
      slug: demoPortal.slug,
      title: demoPortal.title,
      series: demoPortal.series,
      participantName: demoPortal.participantName,
      participantEmail: demoPortal.participantEmail,
      requiresFeatureRelease: demoPortal.requiresFeatureRelease,
      release: demoPortal.legal.featureRelease,
    };
  }
  return apiFetch<ReleasePortal>(`/api/release/${encodeURIComponent(slug)}`);
}

export async function acceptFeatureRelease(slug: string, typedName: string, businessRepresented = ''): Promise<{ acceptedAt: string; typedName: string }> {
  if (demoMode) {
    const acceptedAt = new Date().toISOString();
    localStorage.setItem(`roam-demo-release:${slug}`, JSON.stringify({ acceptedAt, typedName }));
    return { acceptedAt, typedName };
  }
  return apiFetch(`/api/release/${encodeURIComponent(slug)}/accept`, { method: 'POST', body: JSON.stringify({ typedName, businessRepresented }) });
}

export async function acceptPrivateReview(slug: string, typedName: string): Promise<{ acceptedAt: string; typedName: string }> {
  if (demoMode) {
    const acceptedAt = new Date().toISOString();
    localStorage.setItem(`roam-demo-review:${slug}`, JSON.stringify({ acceptedAt, typedName }));
    return { acceptedAt, typedName };
  }
  return apiFetch(`/api/portal/${encodeURIComponent(slug)}/accept-private-review`, { method: 'POST', body: JSON.stringify({ typedName }) });
}

export async function downloadSignedAgreement(slug: string, documentType: 'feature_release' | 'private_review'): Promise<void> {
  if (demoMode) throw new Error('Signed agreement copies require a real authenticated staging account.');
  const accessToken = await token();
  if (!accessToken) throw new Error('Please sign in again to get your agreement copy.');
  const result = await fetch(`${apiBase}/api/portal/${encodeURIComponent(slug)}/agreements/${documentType}/copy`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    cache: 'no-store',
  });
  if (!result.ok) {
    const payload = await result.json().catch(() => ({})) as { error?: string };
    throw new Error(payload.error || 'Unable to retrieve your signed agreement.');
  }
  const blob = await result.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `ROAM-${documentType === 'feature_release' ? 'Participant-Release' : 'Private-Page-Terms'}-signed.html`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Blob URL is only local to this browser tab and does not contain a server access token.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function approvePublication(slug: string): Promise<{ approvedAt: string; typedName: string }> {
  if (demoMode) {
    const approvedAt = new Date().toISOString();
    localStorage.setItem(`roam-demo-approved:${slug}`, JSON.stringify({ approvedAt, typedName: '' }));
    return { approvedAt, typedName: '' };
  }
  return apiFetch(`/api/portal/${encodeURIComponent(slug)}/approve-publication`, { method: 'POST', body: JSON.stringify({ confirmed: true }) });
}

export async function submitReviewCorrection(slug: string, correction: string): Promise<{ ok: boolean }> {
  if (demoMode) return { ok: true };
  return apiFetch(`/api/portal/${encodeURIComponent(slug)}/review-correction`, { method: 'POST', body: JSON.stringify({ correction }) });
}

export async function submitReferral(slug: string, payload: { name: string; email: string; note: string }) {
  if (demoMode) return { ok: true };
  return apiFetch(`/api/portal/${encodeURIComponent(slug)}/referrals`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function checkoutMediaSelects(slug: string, payload: { items: MediaSelectCheckoutItem[]; supportCents?: number; note?: string; typedName: string; licenseAccepted: boolean }) {
  if (demoMode) return { ok: true, orderId: crypto.randomUUID(), acceptedAt: new Date().toISOString(), paymentRequired: false as const };
  return apiFetch<{ ok: boolean; orderId: string; acceptedAt: string; paymentRequired: boolean; checkoutUrl?: string; sessionId?: string }>(`/api/portal/${encodeURIComponent(slug)}/media-select-checkout`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function confirmMediaSelectPayment(slug: string, orderId: string, sessionId: string) {
  if (demoMode) return { ok: true, orderId, paymentStatus: 'paid', paidAt: new Date().toISOString(), totalCents: 0, supportCents: 0 };
  return apiFetch<{ ok: boolean; orderId: string; paymentStatus: string; paidAt?: string; totalCents: number; supportCents?: number }>(`/api/portal/${encodeURIComponent(slug)}/media-select-payment/confirm`, { method: 'POST', body: JSON.stringify({ orderId, sessionId }) });
}

export async function submitCommercialInquiry(slug: string, payload: { projectType: string; details: string; timeline: string; budgetRange: string }) {
  if (demoMode) return { ok: true };
  return apiFetch(`/api/portal/${encodeURIComponent(slug)}/commercial-inquiries`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function createFeature(draft: FeatureDraft): Promise<FeaturePortal> {
  if (demoMode) {
    const feature: FeaturePortal = {
      ...demoPortal,
      id: crypto.randomUUID(),
      ...draft,
      publicStoryUrl: draft.publicStoryUrl,
      media: [],
      legal: {
        ...demoPortal.legal,
        featureRelease: { ...demoPortal.legal.featureRelease, acceptedAt: null, typedName: null },
        privateReview: { ...demoPortal.legal.privateReview, acceptedAt: null, typedName: null },
      },
      publicationApproval: { required: draft.requiresPublicationApproval, approvedAt: null, typedName: null, document: demoPortal.publicationApproval.document },
      requiresFeatureRelease: draft.requiresFeatureRelease,
      status: 'draft',
    };
    localStorage.setItem(`roam-demo-feature:${draft.slug}`, JSON.stringify(feature));
    return feature;
  }
  return apiFetch('/api/admin/features', { method: 'POST', body: JSON.stringify(draft) });
}

export async function updateFeature(featureId: string, draft: FeatureDraft): Promise<FeaturePortal> {
  if (demoMode) return createFeature(draft);
  return apiFetch(`/api/admin/features/${encodeURIComponent(featureId)}`, { method: 'PATCH', body: JSON.stringify(draft) });
}

export async function getAdminActivity(): Promise<AdminActivity> {
  if (demoMode) return { mediaSelectOrders: [], commercialInquiries: [], referrals: [], reviewCorrections: [] };
  return apiFetch('/api/admin/activity');
}

export async function listFeatures(): Promise<FeaturePortal[]> {
  if (demoMode) {
    const extras: FeaturePortal[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('roam-demo-feature:')) {
        const raw = localStorage.getItem(key);
        if (raw) extras.push(JSON.parse(raw) as FeaturePortal);
      }
    }
    return [demoPortal, ...extras.filter((x) => x.slug !== demoPortal.slug)];
  }
  return apiFetch('/api/admin/features');
}

/** Uploads to the authenticated Worker/R2 binding, bypassing S3 presigned-URL CORS.
 *  16 MiB and below use one request; larger files are uploaded as verified 8 MiB
 *  multipart chunks. This is staging-only until a live upload and playback pass.
 */
const INLINE_UPLOAD_LIMIT = 16 * 1024 * 1024;

async function uploadBinary(path: string, chunk: Blob, mimeType: string): Promise<any> {
  const accessToken = await token();
  if (!accessToken) throw new Error('Your administrator session has expired. Sign in again before uploading.');
  // Retries apply to a small file PUT or a numbered multipart part (same part number
  // replaces the old one). Do not retry validation or authorization failures.
  for (let attempt = 0; attempt < 3; attempt++) {
    let response: Response;
    try {
      response = await fetch(`${apiBase}${path}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': mimeType },
        body: chunk,
      });
    } catch (error) {
      if (attempt === 2) throw error;
      await new Promise((resolve) => setTimeout(resolve, 750 * (attempt + 1)));
      continue;
    }
    const raw = await response.text();
    let payload: any;
    try { payload = JSON.parse(raw); } catch { payload = {}; }
    if (response.ok) return payload;
    if ((response.status !== 429 && response.status < 500) || attempt === 2) {
      throw new Error(payload.error || `Private media upload failed (${response.status}).`);
    }
    await new Promise((resolve) => setTimeout(resolve, 750 * (attempt + 1)));
  }
  throw new Error('Private media upload did not complete.');
}

async function uploadThroughWorker(kind: 'media' | 'deliveries', id: string, file: File, onProgress?: (bytes: number) => void) {
  if (!file.size) throw new Error('The selected media file is empty.');
  const base = `/api/admin/${kind}/${encodeURIComponent(id)}`;
  const mime = file.type || 'application/octet-stream';
  onProgress?.(0);
  if (file.size <= INLINE_UPLOAD_LIMIT) {
    await uploadBinary(`${base}/upload`, file, mime);
    onProgress?.(file.size);
    return;
  }
  const session = await apiFetch<{ uploadId: string; partSize: number }>(`${base}/multipart/init`, { method: 'POST', body: '{}' });
  if (!Number.isSafeInteger(session.partSize) || session.partSize < 5 * 1024 * 1024 || session.partSize > 16 * 1024 * 1024) {
    throw new Error('Worker returned an invalid multipart part size.');
  }
  const parts: { partNumber: number; etag: string }[] = [];
  let complete = false;
  try {
    for (let offset = 0, partNumber = 1; offset < file.size; offset += session.partSize, partNumber++) {
      const chunk = file.slice(offset, Math.min(offset + session.partSize, file.size));
      const qs = new URLSearchParams({ uploadId: session.uploadId, partNumber: String(partNumber) });
      const result = await uploadBinary(`${base}/multipart/part?${qs}`, chunk, mime);
      if (result.partNumber !== partNumber || !result.etag) throw new Error('R2 did not confirm an upload part.');
      parts.push({ partNumber, etag: result.etag });
      onProgress?.(Math.min(offset + chunk.size, file.size));
    }
    await apiFetch(`${base}/multipart/complete`, { method: 'POST', body: JSON.stringify({ uploadId: session.uploadId, parts }) });
    complete = true;
  } finally {
    if (!complete) {
      const qs = new URLSearchParams({ uploadId: session.uploadId });
      await apiFetch(`${base}/multipart/abort?${qs}`, { method: 'DELETE' }).catch(() => undefined);
    }
  }
}

export async function uploadMedia(featureId: string, file: File, options: MediaUploadOptions, onProgress?: (bytes: number) => void) {
  if (demoMode) return { ok: true };
  const presign = await apiFetch<{ uploadUrl: string; mediaId: string }>(`/api/admin/features/${featureId}/media/presign`, {
    method: 'POST',
    body: JSON.stringify({
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      bytes: file.size,
      role: options.role,
      title: options.title || file.name,
      description: options.description || '',
      downloadable: options.role === 'feature' ? false : (options.downloadable ?? false),
      freeWatermarked: options.freeWatermarked ?? false,
      cleanPriceCents: options.cleanPriceCents ?? null,
      preparedPriceCents: options.preparedPriceCents ?? null,
    }),
  });
  try {
    await uploadThroughWorker('media', presign.mediaId, file, onProgress);
    return { ok: true, id: presign.mediaId };
  } catch (error) {
    // The media row is created before the browser uploads the object to R2.
    // Always remove that row if the PUT fails or the browser throws a network/CORS error
    // so a participant never sees an orphaned/broken Media Select.
    await apiFetch(`/api/admin/media/${encodeURIComponent(presign.mediaId)}`, { method: 'DELETE' }).catch(() => undefined);
    throw error instanceof Error ? error : new Error('Media upload failed before the file reached storage.');
  }
}


export async function deleteMedia(mediaId: string) {
  if (demoMode) return { ok: true };
  return apiFetch<{ ok: boolean }>(`/api/admin/media/${encodeURIComponent(mediaId)}`, { method: 'DELETE' });
}

export async function getMediaAccessUrl(mediaId: string, disposition: 'inline' | 'download' = 'inline') {
  if (demoMode) return '#';
  const result = await apiFetch<{ url: string }>(`/api/media/${encodeURIComponent(mediaId)}/token?disposition=${disposition}`);
  return result.url.startsWith('http') ? result.url : `${apiBase}${result.url}`;
}

export async function uploadMediaSelectDelivery(requestId: string, file: File) {
  if (demoMode) return { ok: true };
  const presign = await apiFetch<{ uploadUrl: string; deliveryId: string }>(`/api/admin/media-select-requests/${encodeURIComponent(requestId)}/delivery/presign`, {
    method: 'POST',
    body: JSON.stringify({ name: file.name, mimeType: file.type || 'application/octet-stream', bytes: file.size }),
  });
  try {
    await uploadThroughWorker('deliveries', presign.deliveryId, file);
    await apiFetch(`/api/admin/media-select-deliveries/${encodeURIComponent(presign.deliveryId)}/complete`, { method: 'POST', body: '{}' });
    return { ok: true, deliveryId: presign.deliveryId };
  } catch (error) {
    await apiFetch(`/api/admin/media-select-deliveries/${encodeURIComponent(presign.deliveryId)}`, { method: 'DELETE' }).catch(() => undefined);
    throw error instanceof Error ? error : new Error('Delivery upload failed.');
  }
}

export async function deleteMediaSelectDelivery(deliveryId: string) {
  if (demoMode) return { ok: true };
  return apiFetch<{ ok: boolean }>(`/api/admin/media-select-deliveries/${encodeURIComponent(deliveryId)}`, { method: 'DELETE' });
}

export async function getMediaSelectDeliveryUrl(slug: string, orderId: string, requestId: string) {
  if (demoMode) return '#';
  const result = await apiFetch<{ url: string }>(`/api/portal/${encodeURIComponent(slug)}/media-select-orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(requestId)}/download`);
  return result.url.startsWith('http') ? result.url : `${apiBase}${result.url}`;
}
